import { SOREWARDS_PROGRAM_FAMILIES } from '@/models/sorewardsProgramTypes'
import {
  AllocationKind,
  CardKind,
  DotationType,
  IssueTransaction,
  LoadTransaction,
  MappingStatus,
  NegotiatedRates,
  ProgramMapping,
  RateGrid,
  StandardRates
} from '@/services/sorewards/types'

const TX_FAMILIES = ['NOELA', 'SCO', 'NAIS', 'MARPAC', 'CV', 'RETR'] as const

const MOCK_CARETAKERS = [
  'CSE Aubert Industries',
  'Groupe Vallance',
  'Mutuelle Orsène',
  'CSE Ferrand & Fils',
  'Néolia Santé',
  'Cabinet Reynier'
]

const DOTATION_DATES = [
  '2025-10-14',
  '2025-11-03',
  '2025-11-21',
  '2026-01-12',
  '2026-02-09',
  '2026-03-24',
  '2026-05-11',
  '2026-06-02'
]

const STD_DEFAULT: RateGrid = {
  issuePhysical: 4.9,
  issueVirtual: 1.5,
  shipUnit: 2.8,
  shipGrouped: 0.45,
  loadFlat: 0.6,
  loadPct: 1.2
}

type MappingSeed = {
  family: typeof SOREWARDS_PROGRAM_FAMILIES[number]
  allocationKind: AllocationKind
  ceiling: number | null
  validityMonths: number
  status: MappingStatus
}

const MAPPING_SEEDS: MappingSeed[] = [
  { family: 'NOELA', allocationKind: 'both', ceiling: 196, validityMonths: 12, status: 'active' },
  { family: 'NOELE', allocationKind: 'both', ceiling: 196, validityMonths: 12, status: 'active' },
  { family: 'SCO', allocationKind: 'both', ceiling: 196, validityMonths: 12, status: 'active' },
  { family: 'NAIS', allocationKind: 'multi', ceiling: 196, validityMonths: 12, status: 'active' },
  { family: 'MARPAC', allocationKind: 'multi', ceiling: 196, validityMonths: 12, status: 'active' },
  { family: 'CV', allocationKind: 'mono', ceiling: null, validityMonths: 24, status: 'active' },
  { family: 'RETR', allocationKind: 'multi', ceiling: 196, validityMonths: 12, status: 'draft' },
  { family: 'FETPAR', allocationKind: 'multi', ceiling: 196, validityMonths: 12, status: 'active' },
  { family: 'CE', allocationKind: 'both', ceiling: 196, validityMonths: 12, status: 'active' },
  { family: 'CC', allocationKind: 'both', ceiling: 196, validityMonths: 12, status: 'active' },
  { family: 'FL', allocationKind: 'multi', ceiling: 196, validityMonths: 12, status: 'draft' },
  { family: 'PROFID', allocationKind: 'multi', ceiling: null, validityMonths: 12, status: 'draft' },
  { family: 'CHCOMA', allocationKind: 'multi', ceiling: null, validityMonths: 12, status: 'draft' },
  { family: 'PARRAI', allocationKind: 'multi', ceiling: null, validityMonths: 12, status: 'draft' },
  { family: 'INCINT', allocationKind: 'multi', ceiling: null, validityMonths: 12, status: 'draft' },
  { family: 'OEUSOC', allocationKind: 'both', ceiling: 196, validityMonths: 12, status: 'active' },
  { family: 'AUTRES', allocationKind: 'both', ceiling: null, validityMonths: 12, status: 'draft' }
]

function pick<T>(items: readonly T[], index: number): T {
  return items[index % items.length]
}

function pad(value: number, length = 4) {
  return String(value).padStart(length, '0')
}

function addMonths(iso: string, months: number) {
  const date = new Date(`${iso}T00:00:00`)
  date.setMonth(date.getMonth() + months)
  return date.toISOString().slice(0, 10)
}

function validityFor(family: string) {
  return family === 'CV' ? 24 : 12
}

function buildMappings(): ProgramMapping[] {
  return MAPPING_SEEDS.map(seed => ({
    family: seed.family,
    privipassCode: `MOCK-${seed.family}`,
    allocationKind: seed.allocationKind,
    ceiling: seed.ceiling,
    validityMonths: seed.validityMonths,
    status: seed.status
  }))
}

function buildLoads(): LoadTransaction[] {
  return Array.from({ length: 34 }, (_, i) => {
    const programFamily = pick(TX_FAMILIES, i * 2 + Math.floor(i / 3))
    const caretakerName = pick(MOCK_CARETAKERS, i + Math.floor(i / 4))
    const dotationDate = pick(DOTATION_DATES, i)
    const loadDate = `${dotationDate.slice(0, 8)}${pad(Math.min(28, 3 + ((i * 5) % 25)), 2)}`
    const amount = [50, 80, 100, 120, 150, 170, 196][i % 7]
    const spent = [0, 12.4, 35, 58.9, 80, 96.5, 120][(i * 3) % 7]
    const dotationType: DotationType = programFamily === 'CV' || i % 4 === 0 ? 'mono' : 'multi'

    return {
      id: `load-${i + 1}`,
      idDistribution: `DIS-2026-${pad(417 + i * 3)}`,
      caretakerId: '',
      caretakerName,
      programFamily,
      dotationOrder: `CD-${dotationDate.slice(0, 4)}-${pad(842 + i * 7)}`,
      dotationType,
      dotationDate,
      loadDate,
      loadRef: `CHG-${loadDate.slice(0, 4)}-${pad(2201 + i * 11)}`,
      expiry: addMonths(loadDate, validityFor(programFamily)),
      amount,
      beneficiary: `BEN-${pad(104582 + i * 137, 6)}`,
      cardId: `CB-${pad(4471 + i, 4)}-${pad(9 + i * 3, 4)}`,
      remaining: Math.max(0, +(amount - spent).toFixed(2)),
      coverage: [100, 100, 80, 50, 100, 0, 75][i % 7]
    }
  })
}

function buildIssues(loads: LoadTransaction[]): IssueTransaction[] {
  return loads.slice(0, 28).map((row, i) => {
    const cardType: CardKind = i % 3 === 0 ? 'virtual' : 'physical'
    const issueDate = addMonths(row.dotationDate, -1)

    return {
      id: `issue-${i + 1}`,
      cardId: row.cardId,
      caretakerId: '',
      caretakerName: row.caretakerName,
      dotationOrder: row.dotationOrder,
      beneficiary: row.beneficiary,
      issueDate,
      cardNumber: `•••• •••• •••• ${pad(1284 + i * 37, 4).slice(-4)}`,
      expiry: addMonths(issueDate, 36),
      cardType,
      coverage: [100, 100, 50, 100, 0, 80, 100][i % 7]
    }
  })
}

function buildNegotiated(): NegotiatedRates[] {
  return [
    {
      id: 'n1',
      caretakerId: '',
      caretakerName: 'Groupe Vallance',
      issuePhysical: 3.6,
      issueVirtual: 0.9,
      shipUnit: 2.2,
      shipGrouped: 0.3,
      loadFlat: 0.4,
      loadPct: 0.9,
      validUntil: '2027-06-30'
    },
    {
      id: 'n2',
      caretakerId: '',
      caretakerName: 'Mutuelle Orsène',
      issuePhysical: 4.2,
      issueVirtual: 1.2,
      shipUnit: 2.8,
      shipGrouped: 0.38,
      loadFlat: 0.5,
      loadPct: 1.0,
      validUntil: '2026-12-31'
    },
    {
      id: 'n3',
      caretakerId: '',
      caretakerName: 'Néolia Santé',
      issuePhysical: 4.9,
      issueVirtual: 0,
      shipUnit: 3.1,
      shipGrouped: 0.45,
      loadFlat: 0.6,
      loadPct: 0.8,
      validUntil: '2027-03-31'
    }
  ]
}

const loads = buildLoads()

export const mockState = {
  standard: { id: 'standard', ...STD_DEFAULT } as StandardRates,
  negotiated: buildNegotiated(),
  mappings: buildMappings(),
  loads,
  issues: buildIssues(loads)
}

export function cloneGrid(grid: RateGrid): RateGrid {
  return { ...grid }
}
